import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Calendar, Check, X, Upload } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, query, orderBy, updateDoc, doc, deleteDoc, addDoc } from 'firebase/firestore';

export default function AdminEvents() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [refetch, setRefetch] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'event',
    image_url: '',
    discount_rate: null,
    is_active: true,
    is_popup: false,
    start_date: '',
    end_date: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);

        const eventsQuery = query(collection(db, 'events'), orderBy('created_at', 'desc'));
        const querySnapshot = await getDocs(eventsQuery);
        const eventsData = [];
        querySnapshot.forEach((doc) => {
          eventsData.push({ id: doc.id, ...doc.data() });
        });

        setEvents(eventsData);
      } catch (err) {
        console.warn('Events fetch failed:', err);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [refetch]);

  // 이벤트 상태 업데이트
  const toggleEvent = async ({ id, is_active }) => {
    try {
      const eventDoc = doc(db, 'events', id);
      await updateDoc(eventDoc, { is_active: !is_active });
      setRefetch(c => c + 1); // 목록 새로고침
    } catch (error) {
      console.error('Event toggle failed:', error);
      alert('이벤트 상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 이벤트 삭제
  const deleteEvent = async (eventId) => {
    try {
      const eventDoc = doc(db, 'events', eventId);
      await deleteDoc(eventDoc);
      setRefetch(c => c + 1); // 목록 새로고침
    } catch (error) {
      console.error('Event delete failed:', error);
      alert('이벤트 삭제 중 오류가 발생했습니다.');
    }
  };

  const openModal = (event) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        description: event.description || '',
        event_type: event.event_type,
        image_url: event.image_url || '',
        discount_rate: event.discount_rate,
        is_active: event.is_active,
        is_popup: event.is_popup,
        start_date: event.start_date.split('T')[0],
        end_date: event.end_date.split('T')[0]
      });
      setImagePreview(event.image_url || '');
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        event_type: 'event',
        image_url: '',
        discount_rate: null,
        is_active: true,
        is_popup: false,
        start_date: '',
        end_date: ''
      });
      setImagePreview('');
    }
    setSelectedImage(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setSelectedImage(null);
    setImagePreview('');
  };

  // 이미지 파일 선택 처리
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('이미지 파일 크기는 5MB 이하여야 합니다.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const uploadImageAndGetURL = (file) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/upload?filename=${file.name}`, true);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response.url);
          } catch (e) {
            reject(new Error('서버 응답 파싱에 실패했습니다.'));
          }
        } else {
          reject(new Error(`파일 업로드 실패: ${xhr.status} ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error('네트워크 오류로 파일 업로드에 실패했습니다.'));
      xhr.send(file);
    });
  };

  const createEvent = async (newEvent) => {
    try {
      await addDoc(collection(db, 'events'), newEvent);
      setRefetch(c => c + 1); // 목록 새로고침
      closeModal();
    } catch (error) {
      console.error('Event create failed:', error);
      alert('이벤트 생성 중 오류가 발생했습니다.');
    }
  };

  const updateEvent = async (updatedEvent) => {
    try {
      const eventId = updatedEvent.id;
      const eventDataForUpdate = { ...updatedEvent };
      delete eventDataForUpdate.id;
      delete eventDataForUpdate.created_at; // Prevent modification of created_at

      const eventDoc = doc(db, 'events', eventId);
      await updateDoc(eventDoc, eventDataForUpdate);
      
      setRefetch(c => c + 1); // 목록 새로고침
      closeModal();
    } catch (error) {
      console.error('Event update failed:', error);
      alert('이벤트 수정 중 오류가 발생했습니다.');
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.start_date || !formData.end_date) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    setIsSaving(true);
    try {
      let imageUrl = formData.image_url;

      if (selectedImage) {
        imageUrl = await uploadImageAndGetURL(selectedImage);
      }

      const eventData = {
        ...formData,
        image_url: imageUrl,
        created_at: editingEvent ? editingEvent.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (editingEvent) {
        await updateEvent({
          id: editingEvent.id,
          ...eventData
        });
      } else {
        await createEvent(eventData);
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">이벤트 관리</h1>
          <p className="text-sm text-gray-600 mt-1">각종 프로모션 및 이벤트를 관리합니다</p>
        </div>
        <div className="flex space-x-3">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
            onClick={() => openModal()}
          >
            <Plus className="w-4 h-4 mr-2" />
            새 이벤트 추가
          </button>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">등록된 이벤트가 없습니다.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <div 
              key={event.id} 
              className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-shadow duration-300 hover:shadow-md"
              onClick={() => window.open(`/event/${event.id}`, '_blank')}
            >
              {event.image_url && (
                <img
                  src={event.image_url}
                  alt={event.title}
                  className="w-full h-48 object-cover"
                />
              )}

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${event.event_type === 'discount' ? 'bg-red-100 text-red-800' :
                      event.event_type === 'special' ? 'bg-purple-100 text-purple-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                      {event.event_type === 'discount' ? '할인' :
                        event.event_type === 'special' ? '특별' : '이벤트'}
                      {event.discount_rate && ` ${event.discount_rate}%`}
                    </span>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                      {event.is_active ? '활성' : '비활성'}
                    </span>
                    {event.is_popup && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        팝업
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {event.title}
                </h3>

                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {event.description}
                </p>

                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>
                    {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleEvent(event); }}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${event.is_active
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                  >
                    {event.is_active ? <X className="w-3 h-3 inline mr-1" /> : <Check className="w-3 h-3 inline mr-1" />}
                    {event.is_active ? '비활성화' : '활성화'}
                  </button>

                  <button
                    className="px-3 py-2 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    onClick={(e) => { e.stopPropagation(); openModal(event); }}
                  >
                    <Edit className="w-3 h-3 inline mr-1" />
                    수정
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('정말로 이 이벤트를 삭제하시겠습니까?')) {
                        deleteEvent(event.id);
                      }
                    }}
                    className="px-3 py-2 rounded text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" />
                    삭제
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{editingEvent ? '이벤트 수정' : '새 이벤트 추가'}</h3>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">이벤트 이미지</label>

                  {/* 이미지 미리보기 */}
                  {imagePreview && (
                    <div className="mb-3">
                      <img
                        src={imagePreview}
                        alt="미리보기"
                        className="w-full h-32 object-cover rounded-md border border-gray-300"
                      />
                    </div>
                  )}

                  {/* 파일 업로드 버튼 */}
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                      <Upload className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-700">이미지 선택</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>

                    {selectedImage && (
                      <span className="text-sm text-gray-600">
                        {selectedImage.name}
                      </span>
                    )}
                  </div>

                  {/* URL 직접 입력 옵션 */}
                  <div className="mt-3">
                    <label className="block text-xs text-gray-500 mb-1">또는 이미지 URL 직접 입력</label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => {
                        setFormData({ ...formData, image_url: e.target.value });
                        if (e.target.value) {
                          setImagePreview(e.target.value);
                          setSelectedImage(null);
                        }
                      }}
                      placeholder="https://example.com/image.jpg"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    • 지원 형식: JPG, PNG, GIF, WebP
                    • 최대 파일 크기: 5MB
                    • 권장 크기: 800x400px
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이벤트 유형 *</label>
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="event">일반 이벤트</option>
                    <option value="discount">할인 이벤트</option>
                    <option value="special">특별 이벤트</option>
                  </select>
                </div>

                {formData.event_type === 'discount' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">할인율 (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.discount_rate || ''}
                      onChange={(e) => setFormData({ ...formData, discount_rate: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder="예: 20"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">시작일 *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">종료일 *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>


                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">활성화 상태</label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_popup"
                      checked={formData.is_popup}
                      onChange={(e) => setFormData({ ...formData, is_popup: e.target.checked })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="is_popup" className="ml-2 block text-sm text-gray-700">팝업으로 표시</label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
